//@flow
import React, { Component } from 'react';
import type { Node } from 'react';
import { connect } from 'react-redux';
import injectSheet from 'react-jss';
import { isExploreYourPlanM3Enabled, sendTrackingEvent } from 'h-commons';
import { Button, CircularProgress } from '@hiver/hiver-ui-kit';
import { isEmpty } from 'lodash';
import SVGInline from 'react-svg-inline';

import modalCss from '../styles/modal';
import disabledBtn from '../images/disabled.svg';
import closeBtn from '../images/close.svg';
import actionRequiredIcon from '../images/actionRequired.svg';
import alertOrangeIcon from '../images/alertOrange.svg';
import { HAS_EXTENDED_TRIAL_BEFORE } from '../../sharedmailbox/common/constants';
import { EXTEND_TRIAL_GAINSIGHT_EVENT_KEY, EXTEND_TRIAL_SUPPORT_GS_EVENT_KEY, HCX_TRIAL_PLAN, UG_STATE } from '../constants';
import { getHiverChatSupportLink, setSessionIdToUrl } from '../helpers';
import { useExtendUgTrialApi } from '../../settings/components/Account/ExplorePlans/api/useExtendUgTrialApi';

/**
* This file is responsible for displaying a reusable modal
* @author Hrushikesh
*/
type State = {
    openModal: boolean
}
type Props = {
    classes: Object,
    shown: boolean,
    exceededFeaturesLimit: Object,
    unavailableFeatures: Array,
    handleModalState: Function

}

const PROCEED_BUTTON_TEXT = 'Okay, proceed';
const ACKNOWLEDGE_BUTTON_TEXT = 'Got it';
const UNAVAILABLE_FEATURES_WARNING = 'You will lose access to the following features as they are not available on your selected plan.';
const EXCEEDED_FEATURES_LIMIT_WARNING = 'Please reduce usage on the following features to the specified limits to continue with the plan change';
const SECTION_HEADING = {
    FEATURES_SECTION: {
        TITLE: 'Note',
        ICON: alertOrangeIcon
    },
    LIMITS_SECTION: {
        TITLE: 'Action required',
        ICON: actionRequiredIcon
    },
};

const SectionHeading = ({ showHeading, sectionHeading }) => {
    if (showHeading) {
    return (
        <div className='body-section-heading'>
            <SVGInline svg={sectionHeading.ICON} width="18px" height="18px" /> 
            <span>{sectionHeading.TITLE}</span>
        </div>);
    }
}

const UnavailableFeaturesSectionBody = ({ unavailableFeatures }) => (
    <div className="wrapper">
        <div id="features-lost">
            {unavailableFeatures.map((element) => (
                <div className="feature-lost" key={element} >
                    <SVGInline svg={disabledBtn} className="disabled-btn" />
                    <span className="feature">{element}</span>
                </div>)
            )}
        </div>
    </div>
);

const ExceededFeaturesLimitSectionBody = ({ exceededFeaturesLimit }) => (
    <div className="wrapper">
        <div id="exceeded-features">
            {Object.keys(exceededFeaturesLimit).map((feature) => (
                <div className="exceeded-feature" key={feature} >
                    {exceededFeaturesLimit[feature]} {feature}
                </div>)
            )}
        </div>
    </div>
)

const PlanChangeModalContent = ({ exceededFeaturesLimit, unavailableFeatures }) => {
    const hasExceededFeaturesLimit = Object.keys(exceededFeaturesLimit).length > 0;
    const hasUnavailableFeatures = unavailableFeatures.length > 0;
    const areBothFeatureLimitsExceeded = hasExceededFeaturesLimit && hasUnavailableFeatures;
    return (
        <div id="body-section" className={areBothFeatureLimitsExceeded ? 'wide-body-section' : ''}>
            { hasExceededFeaturesLimit ? 
                <div id="limits-section">
                    <SectionHeading showHeading={areBothFeatureLimitsExceeded} sectionHeading={SECTION_HEADING.LIMITS_SECTION} />
                    <p className="section-warning-message">{EXCEEDED_FEATURES_LIMIT_WARNING}</p>
                    <ExceededFeaturesLimitSectionBody exceededFeaturesLimit={exceededFeaturesLimit} />
                </div> : null
            }
            { hasUnavailableFeatures ? 
                <div id="features-section" className={areBothFeatureLimitsExceeded ? 'section-divider' : ''}>
                    <SectionHeading showHeading={areBothFeatureLimitsExceeded} sectionHeading={SECTION_HEADING.FEATURES_SECTION} />
                    <p className="section-warning-message">{UNAVAILABLE_FEATURES_WARNING}</p>
                    <UnavailableFeaturesSectionBody unavailableFeatures={unavailableFeatures} />
                </div> : null 
            }
        </div>
    );
}

const ActionButtons = ({ isExplorePlanM3Enabled, exceededFeaturesLimit, onProceedClick, onCancelClick, userDetails }) => {
    const { ugDetails, usergroupProperties, planDetails, usergroupId, userId, firstName, emailId } = userDetails;
    const { mutateAsync: extendTrialMutate, isPending } = useExtendUgTrialApi();
    // If there are no limit exceeded features, the user can proceed or else user has to acknoledge the blocker
    const planChangeCTAButtonLabel = isEmpty(exceededFeaturesLimit) ? PROCEED_BUTTON_TEXT  : ACKNOWLEDGE_BUTTON_TEXT;
    const onPlanChangeCTAClickHandler = isEmpty(exceededFeaturesLimit) ?  onProceedClick : onCancelClick;
    // Extending trial is only allowed when account is suspended from trial plan ,has exceeded some feature limits and has not used extend trial from admin-panel before
    const showExtendTrialButton = planDetails.planid === HCX_TRIAL_PLAN && 
                                  ugDetails.state === UG_STATE.SUSPENDED && 
                                  !isEmpty(exceededFeaturesLimit) && 
                                  usergroupProperties?.[HAS_EXTENDED_TRIAL_BEFORE] !== '1';
    const onExtendTrialClick = () => {
        extendTrialMutate({usergroupid: usergroupId}).then(() => {
            const url = '/app/settings/account';
            const refreshLink = setSessionIdToUrl(url, userId);
            window.location.replace(refreshLink);
        })
        sendTrackingEvent(EXTEND_TRIAL_GAINSIGHT_EVENT_KEY);
    }
    if (isExplorePlanM3Enabled) {
        return (
            <>
                {showExtendTrialButton && (
                    <Button
                        size="medium"
                        color="tertiary"
                        variant="outlined"
                        onClick={onExtendTrialClick}
                        sx={{ marginRight: 1.5 }}
                        >
                        {isPending ? <CircularProgress sx={{color: "#000"}} size={20}/> : "Extend Trial"}
                    </Button>
                )}
                <Button
                    size="medium"
                    color="tertiary"
                    sx={{ marginRight: "auto" }}
                    variant="outlined"
                    onClick={() => sendTrackingEvent(EXTEND_TRIAL_SUPPORT_GS_EVENT_KEY)}
                    href={getHiverChatSupportLink(emailId, firstName)}
                    target="_blank"
                    rel="noopener noreferrer"
                    >
                    Contact Support
                </Button>
                <Button size="medium" color="tertiary" variant="outlined" onClick={onCancelClick}>Cancel</Button>
                {!showExtendTrialButton && <Button size="medium" color="tertiary" sx={{ marginLeft: 1.5 }} onClick={onPlanChangeCTAClickHandler}>{planChangeCTAButtonLabel}</Button>}
            </>);
    }
    return <button className="h-primary-button" onClick={onCancelClick}>Got it</button>
}

class PlanChangeInfoModal extends Component<Props, State> {

    constructor(props) {
        super(props);
    }

    closeModal = () => {
        this.props.handleModalState(false);
    }

     onProceedClick = () => {
        this.closeModal();
        this.props.proceedToBilling();
    }

    render() {
        const { shown, userDetails } = this.props;
        if(!shown) {
            return null;
        }
        const { 
            classes,
            exceededFeaturesLimit,
            unavailableFeatures,
        } = this.props;
        let containerClass = classes.modalCont;
        const hasExceededFeaturesLimit = Object.keys(exceededFeaturesLimit).length > 0;
        const hasUnavailableFeatures = unavailableFeatures.length > 0;
        const areBothFeatureLimitsExceeded = hasExceededFeaturesLimit && hasUnavailableFeatures;
        const isExplorePlanM3Enabled = isExploreYourPlanM3Enabled(this.props.availableFeatures);

        return (
            <div className={`${classes.modal} ${isExplorePlanM3Enabled? 'explore-your-plan-modal' : ''}`}>
                <div className={containerClass} id="confirm-downgrade">
                    {!isExplorePlanM3Enabled ?
                        <div id="close-btn-wrapper" onClick={this.closeModal}><SVGInline svg={closeBtn} id="close-btn" /></div>
                        : null }
                    <div className={classes.modalContent} id="text">
                        <p id="heading">Review feature usage</p>
                        {isExplorePlanM3Enabled ? <PlanChangeModalContent {...{exceededFeaturesLimit, unavailableFeatures}} /> : 
                        <>
                            <p id="limit-exceeded-msg">Some of your current features in use need to be reviewed before you can proceed with the plan you’ve selected.</p>
                            <div id="body-section">
                                { hasExceededFeaturesLimit ? 
                                <div id="limits-section">
                                    <p className="section-warning-message"><span className="subheading">Limits: </span>Please reduce your usage on the following features to the specified limits to continue with this plan change:</p>
                                    <ExceededFeaturesLimitSectionBody exceededFeaturesLimit={exceededFeaturesLimit} />
                                </div> : null
                                }
                                { hasUnavailableFeatures ? 
                                <div id="features-section" className={areBothFeatureLimitsExceeded ? 'section-divider' : ''}>
                                    <p className="section-warning-message"><span className="subheading">Feature usage: </span>The following features are not supported in the plan you’ve selected. Please disable/delete them to proceed.</p>
                                    <UnavailableFeaturesSectionBody unavailableFeatures={unavailableFeatures} />
                                </div> : null 
                                }
                            </div> 
                        </>}
                    </div>
                    <div className={classes.modalAction} id="action-buttons">
                        <ActionButtons {...{isExplorePlanM3Enabled, exceededFeaturesLimit, onProceedClick: this.onProceedClick, onCancelClick: this.closeModal, userDetails}} />
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = ({ userDetails }) => ({
    availableFeatures: userDetails.availableFeatures,
    userDetails
})

export default connect(mapStateToProps)(injectSheet(modalCss)(PlanChangeInfoModal));